import { Button } from '@/components/ui/Button'
import { SessionExpiredError, toApiError } from '@/lib/api/errors'

/**
 * 조회 실패 공통 표현.
 *
 * 🚨 상태 코드마다 **원인과 사용자가 할 일이 다르다.** 하나의 "오류가 발생했습니다"로 뭉개면
 * 남의 구장을 본 것(403)인지 없는 구장(404)인지 네트워크 문제인지 구분할 수 없다.
 * 제목은 상태별로 고정하고, 본문은 **서버가 준 `errorMsg` 를 그대로** 보여준다
 * (`toApiError` 가 서버 문구가 없을 때만 상태별 기본 문구를 채운다).
 */
function titleFor(status: number): string {
  switch (status) {
    case 0:
      return '서버에 연결하지 못했습니다'
    case 401:
      return '로그인이 필요합니다'
    case 403:
      return '접근 권한이 없습니다'
    case 404:
      return '대상을 찾을 수 없습니다'
    default:
      return '요청을 처리하지 못했습니다'
  }
}

export function ErrorState({
  error,
  onRetry,
  retryLabel = '다시 시도',
}: {
  error: unknown
  onRetry?: () => void
  retryLabel?: string
}) {
  // 세션 만료는 인터셉터가 이미 로그인 화면으로 보내는 중이다 — 재시도 버튼을 주지 않는다.
  const isSessionExpired = error instanceof SessionExpiredError
  const apiError = toApiError(error)
  const status = isSessionExpired ? 401 : apiError.status

  return (
    <div
      role="alert"
      className="flex flex-col items-center justify-center gap-2 rounded-xl border border-slateish-200 bg-white px-5 py-12 text-center"
    >
      <p className="text-sm font-semibold text-ink-700">{titleFor(status)}</p>
      <p className="text-sm text-slateish-600">{apiError.message}</p>
      {onRetry && !isSessionExpired ? (
        <Button variant="secondary" size="sm" className="mt-2" onClick={onRetry}>
          {retryLabel}
        </Button>
      ) : null}
    </div>
  )
}
